import { roundMoney } from "@/domain/money/round-money";
import type {
  CartLineCapture,
  CartValidityInput,
  CartValidityIssue,
  CartValidityLineView,
  CartValidityStatus,
  CartValidityViewModel,
  MenuCatalogItem,
  MenuCatalogSnapshot,
} from "@/domain/cart/cart-validity.types";
import { deriveValidityPresentation } from "@/domain/cart/cart-validity.presentation";
import type { CartLine } from "@/shared/cart/model.types";

const MONEY_EPSILON = 0.005;

function isExplicitCapture(captures: CartLineCapture[], clientItemId: string): boolean {
  return captures.some((capture) => capture.client_item_id === clientItemId);
}

function resolveLineCapture(
  captures: CartLineCapture[],
  line: CartLine,
  catalog: MenuCatalogSnapshot
): CartLineCapture {
  const explicit = captures.find((capture) => capture.client_item_id === line.client_item_id);
  if (explicit) return explicit;

  const menuItem = findCatalogItem(catalog, line.menu_item_id);
  return {
    client_item_id: line.client_item_id,
    capturedUnitPrice: roundMoney(line.item_price),
    capturedMenuVersion: menuItem?.menu_version ?? 0,
    capturedCatalogFingerprint: "",
    capturedModifierIds: collectModifierIds(line),
  };
}

function collectModifierIds(line: CartLine): string[] {
  const ids: string[] = [];
  for (const group of line.selected_modifiers ?? []) {
    for (const mod of group.items ?? []) {
      const id = String(mod.modifier_group_item_id ?? "").trim();
      if (id) ids.push(id);
    }
  }
  return ids.sort();
}

function findCatalogItem(catalog: MenuCatalogSnapshot, menuItemId: string): MenuCatalogItem | undefined {
  const id = String(menuItemId);
  return catalog.items.find((item) => String(item.menu_item_id) === id);
}

function isPriceEqual(a: number, b: number): boolean {
  return Math.abs(roundMoney(a) - roundMoney(b)) < MONEY_EPSILON;
}

function issueKey(issue: CartValidityIssue): string {
  return [
    issue.code,
    issue.client_item_id ?? "",
    issue.menu_item_id ?? "",
    issue.modifier_group_item_id ?? "",
  ].join("|");
}

function dedupeIssues(issues: CartValidityIssue[]): CartValidityIssue[] {
  const seen = new Set<string>();
  const out: CartValidityIssue[] = [];
  for (const issue of issues) {
    const key = issueKey(issue);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(issue);
  }
  return out;
}

function resolveCapturedCatalogFingerprint(
  captures: CartLineCapture[],
  cartLines: CartLine[]
): string | null {
  for (const line of cartLines) {
    const capture = captures.find((entry) => entry.client_item_id === line.client_item_id);
    if (capture?.capturedCatalogFingerprint) {
      return capture.capturedCatalogFingerprint;
    }
  }
  return null;
}

function resolveLineStatus(issues: CartValidityIssue[]): CartValidityLineView["lineStatus"] {
  if (issues.some((issue) => issue.severity === "blocking")) return "INVALID";
  if (issues.some((issue) => issue.severity === "recoverable")) return "STALE";
  return "VALID";
}

function resolveAggregateStatus(
  catalog: MenuCatalogSnapshot,
  issues: CartValidityIssue[]
): CartValidityStatus {
  if (!catalog.restaurantAvailable) return "RESTAURANT_UNAVAILABLE";
  if (!catalog.loaded) return "UNKNOWN";
  if (issues.some((issue) => issue.severity === "blocking")) return "INVALID";
  if (issues.some((issue) => issue.severity === "recoverable")) return "STALE";
  return "VALID";
}

function pushIssue(bucket: CartValidityIssue[], issue: CartValidityIssue): void {
  bucket.push(issue);
}

function detectComboChildIssues(
  line: CartLine,
  menuItem: MenuCatalogItem | undefined,
  catalog: MenuCatalogSnapshot,
  issues: CartValidityIssue[]
): void {
  const combos = line.selectedCombos ?? [];
  if (combos.length === 0) return;

  const componentChildIds = new Set(
    (menuItem?.components ?? []).map((component) => String(component.child_menu_item_id))
  );
  const catalogIds = new Set(catalog.items.map((item) => String(item.menu_item_id)));

  for (const combo of combos) {
    const childId = String(combo.child_menu_item_id ?? "").trim();
    if (!childId) continue;

    const inComponents = componentChildIds.has(childId);
    const inCatalog = catalogIds.has(childId);
    if (!inComponents && !inCatalog) {
      pushIssue(issues, {
        code: "COMBO_CHILD_REMOVED",
        severity: "blocking",
        message: "Combo selection is no longer available",
        client_item_id: line.client_item_id,
        menu_item_id: String(line.menu_item_id),
      });
    }
  }
}

function detectModifierIssues(
  line: CartLine,
  menuItem: MenuCatalogItem | undefined,
  capture: CartLineCapture,
  hasExplicitCapture: boolean,
  issues: CartValidityIssue[]
): void {
  for (const group of line.selected_modifiers ?? []) {
    const groupId = String(group.modifier_group_id ?? "").trim();
    if (!groupId) continue;

    const catalogGroup = menuItem?.modifier_groups?.find(
      (entry) => String(entry.modifier_group_id) === groupId
    );

    if (!catalogGroup) {
      pushIssue(issues, {
        code: "MODIFIER_GROUP_REMOVED",
        severity: "blocking",
        message: "Modifier group is no longer on the menu",
        client_item_id: line.client_item_id,
        menu_item_id: String(line.menu_item_id),
      });
      continue;
    }

    for (const mod of group.items ?? []) {
      const modifierId = String(mod.modifier_group_item_id ?? "").trim();
      if (!modifierId) continue;

      const catalogMod = catalogGroup.items?.find(
        (entry) => String(entry.modifier_group_item_id) === modifierId
      );

      if (!catalogMod) {
        pushIssue(issues, {
          code: "MODIFIER_REMOVED",
          severity: "blocking",
          message: "Modifier option is no longer on the menu",
          client_item_id: line.client_item_id,
          menu_item_id: String(line.menu_item_id),
          modifier_group_item_id: modifierId,
        });
        continue;
      }

      if (catalogMod.is_effectively_selectable === false) {
        pushIssue(issues, {
          code: "MODIFIER_UNAVAILABLE",
          severity: "blocking",
          message: "Modifier option is currently unavailable",
          client_item_id: line.client_item_id,
          menu_item_id: String(line.menu_item_id),
          modifier_group_item_id: modifierId,
        });
      }

      const currentDelta = roundMoney(catalogMod.delta_price);
      const capturedDelta = roundMoney(mod.delta_price);
      if (!isPriceEqual(currentDelta, capturedDelta)) {
        pushIssue(issues, {
          code: "STALE_PRICE",
          severity: "recoverable",
          message: "Modifier price has changed",
          client_item_id: line.client_item_id,
          menu_item_id: String(line.menu_item_id),
          modifier_group_item_id: modifierId,
          context: {
            expectedPrice: capturedDelta,
            currentPrice: currentDelta,
          },
        });
      }
    }
  }

  if (hasExplicitCapture) {
    const currentModifierIds = new Set(collectModifierIds(line));
    for (const capturedId of capture.capturedModifierIds) {
      if (!currentModifierIds.has(capturedId)) {
        pushIssue(issues, {
          code: "MODIFIER_REMOVED",
          severity: "blocking",
          message: "Modifier option is no longer on the menu",
          client_item_id: line.client_item_id,
          menu_item_id: String(line.menu_item_id),
          modifier_group_item_id: capturedId,
        });
      }
    }
  }
}

function detectLineIssues(
  line: CartLine,
  catalog: MenuCatalogSnapshot,
  capture: CartLineCapture,
  hasExplicitCapture: boolean
): CartValidityIssue[] {
  const issues: CartValidityIssue[] = [];
  const menuItemId = String(line.menu_item_id);
  const menuItem = findCatalogItem(catalog, menuItemId);

  if (!menuItem || menuItem.is_deleted === true) {
    pushIssue(issues, {
      code: "ITEM_REMOVED",
      severity: "blocking",
      message: "Item is no longer on the menu",
      client_item_id: line.client_item_id,
      menu_item_id: menuItemId,
    });
    return issues;
  }

  if (menuItem.is_available === false) {
    pushIssue(issues, {
      code: "ITEM_UNAVAILABLE",
      severity: "blocking",
      message: "Item is currently unavailable",
      client_item_id: line.client_item_id,
      menu_item_id: menuItemId,
    });
  }

  const currentPrice = roundMoney(menuItem.item_price);
  const capturedPrice = roundMoney(capture.capturedUnitPrice);
  if (!isPriceEqual(currentPrice, capturedPrice)) {
    pushIssue(issues, {
      code: "STALE_PRICE",
      severity: "recoverable",
      message: "Item price has changed",
      client_item_id: line.client_item_id,
      menu_item_id: menuItemId,
      context: {
        expectedPrice: capturedPrice,
        currentPrice,
      },
    });
  }

  if (
    hasExplicitCapture &&
    capture.capturedMenuVersion > 0 &&
    capture.capturedMenuVersion !== menuItem.menu_version
  ) {
    pushIssue(issues, {
      code: "MENU_VERSION_CHANGED",
      severity: "recoverable",
      message: "Menu version has changed for this item",
      client_item_id: line.client_item_id,
      menu_item_id: menuItemId,
      context: {
        capturedMenuVersion: capture.capturedMenuVersion,
        currentMenuVersion: menuItem.menu_version,
      },
    });
  }

  detectModifierIssues(line, menuItem, capture, hasExplicitCapture, issues);
  detectComboChildIssues(line, menuItem, catalog, issues);

  return issues;
}

function detectCatalogIssues(catalog: MenuCatalogSnapshot): CartValidityIssue[] {
  const issues: CartValidityIssue[] = [];

  if (!catalog.loaded) {
    pushIssue(issues, {
      code: "CATALOG_NOT_LOADED",
      severity: "informational",
      message: "Menu catalog is not loaded",
    });
  }

  if (!catalog.restaurantAvailable) {
    pushIssue(issues, {
      code: "RESTAURANT_UNAVAILABLE",
      severity: "blocking",
      message: "Restaurant is not accepting orders",
    });
  }

  return issues;
}

/**
 * Resolves cart validity against a menu catalog snapshot.
 * Pure — no store, hooks, or React.
 */
export function resolveCartValidity(input: CartValidityInput): CartValidityViewModel {
  const { cartLines, catalog, captures } = input;
  const catalogIssues = detectCatalogIssues(catalog);
  const lineViews: CartValidityLineView[] = [];
  const lineIssues: CartValidityIssue[] = [];

  if (catalog.loaded && catalog.restaurantAvailable) {
    for (const line of cartLines) {
      const hasExplicitCapture = isExplicitCapture(captures, line.client_item_id);
      const capture = resolveLineCapture(captures, line, catalog);
      const issues = detectLineIssues(line, catalog, capture, hasExplicitCapture);
      lineIssues.push(...issues);

      const menuItem = findCatalogItem(catalog, line.menu_item_id);
      lineViews.push({
        client_item_id: line.client_item_id,
        menu_item_id: String(line.menu_item_id),
        item_name: line.item_name,
        lineStatus: resolveLineStatus(issues),
        issues,
        currentUnitPrice: menuItem ? roundMoney(menuItem.item_price) : null,
        capturedUnitPrice: roundMoney(capture.capturedUnitPrice),
      });
    }
  }

  const capturedFingerprint = resolveCapturedCatalogFingerprint(captures, cartLines);
  const catalogVersionChanged =
    Boolean(capturedFingerprint) && capturedFingerprint !== catalog.fingerprint;

  if (catalogVersionChanged && catalog.loaded) {
    catalogIssues.push({
      code: "MENU_VERSION_CHANGED",
      severity: "recoverable",
      message: "Menu catalog has changed since this cart was built",
      context: {
        catalogFingerprint: catalog.fingerprint,
        capturedCatalogFingerprint: capturedFingerprint ?? undefined,
      },
    });
  }

  const issues = dedupeIssues([...catalogIssues, ...lineIssues]);
  const status = resolveAggregateStatus(catalog, issues);

  const catalogView = {
    loaded: catalog.loaded,
    restaurantAvailable: catalog.restaurantAvailable,
    fingerprint: catalog.fingerprint,
    capturedFingerprint,
    catalogVersionChanged,
  };

  const base = {
    status,
    issues,
    lines: lineViews,
    catalog: catalogView,
  };

  return {
    ...base,
    presentation: deriveValidityPresentation(base),
  };
}
